import React, { useState, useEffect, useMemo } from "react";
import {
  Pencil,
  Eye,
  Plus,
  Search,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useCachedDoctorsSimple } from "../../hooks/useCachedSimple";
import { API_ENDPOINTS } from "../../services/api/apiConfig";
import Header from "../common/Header";
import LoadingScreen from "../common/LoadingScreen";
import ResponseMessage from "../common/ResponseMessage";
import type { Consultation, Doctor, MedicalRecord } from "../../types";
import axiosInstance from "../../services/axiosInstance";

const PatientForm: React.FC = () => {
  const [consultations, setConsultations] = useState<Consultation[]>([]);
  const [doctors, setDoctors] = useState<Doctor[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");

  // Utiliser le hook de cache pour les médecins
  const { doctors: cachedDoctors, loading: doctorsLoading, error: doctorsError, refetch: refetchDoctors } = useCachedDoctorsSimple();
  const navigate = useNavigate();

  // Synchroniser les états avec le hook de cache
  useEffect(() => {
    setDoctors(cachedDoctors);
    setLoading(doctorsLoading);
    setError(doctorsError);
  }, [cachedDoctors, doctorsLoading, doctorsError]);

  // Date filter state
  const [dateFilterMode, setDateFilterMode] = useState<
    "today" | "custom" | "all"
  >("today");
  const todayYMD = new Date().toISOString().slice(0, 10);
  const [selectedDate, setSelectedDate] = useState<string>(todayYMD);

  // Field-specific filters
  const [filterMontant, setFilterMontant] = useState<number | null>(null);
  const [filterPaye, setFilterPaye] = useState<boolean | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const recordsPerPage = 10;
  const [phoneFilter, setPhoneFilter] = useState("");
  const [motifFilter, setMotifFilter] = useState("");
  const [nameFilter, setNameFilter] = useState("");
  const [doctorFilter, setDoctorFilter] = useState<string>("");

  // Debounce search term to reduce API calls
  const debouncedSearchTerm = useMemo(() => {
    const handler = setTimeout(() => searchTerm, 500);
    return () => clearTimeout(handler);
  }, [searchTerm]);

  // Reset to first page when search term changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  useEffect(() => {
    fetchConsultations();
    fetchDoctors();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, debouncedSearchTerm]);

  const fetchDoctors = async () => {
    // Utiliser le hook de cache - les données sont déjà synchronisées
    // La fonction est maintenant gérée par le hook useCachedDoctorsSimple
    refetchDoctors();
  };

  const fetchConsultations = async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) throw new Error("No token found");

      // Use server-side pagination and search endpoint
      const response = await axiosInstance.get(
        `${API_ENDPOINTS.MEDICAL_RECORDS.LIST}/search`,
        {
          params: {
            query: searchTerm,
            page: currentPage - 1,
            size: recordsPerPage,
          },
        }
      );

      let recordsData: any[] = [];
      let totalPagesData = 1;

      // Handle Page<MedicalRecordResponseDTO> structure
      if (response.data && Array.isArray(response.data.content)) {
        recordsData = response.data.content as MedicalRecord[];
        totalPagesData = response.data.totalPages;
      } else if (Array.isArray(response.data)) {
        // Fallback for non-paginated response
        recordsData = response.data;
        totalPagesData = Math.ceil(response.data.length / recordsPerPage);
      } else {
        // Fallback for other structures
        recordsData = response.data.records || [];
        totalPagesData = response.data.totalPages || 1;
      }

      // Map records using enriched DTO data - NO MORE N+1 QUERIES!
      const consultationsWithInfo = recordsData.map((record: any) => {
        // Backend now provides patientFullName, doctorFullName, and phone directly
        const fullName = record.patientFullName || "Patient non assigné";
        const phone = record.patientPhone || record.phone || "N/A";
        const doctorName = record.doctorFullName || "N/A";

        // Split fullName for firstName/lastName compatibility
        const nameParts = fullName.split(" ");
        const firstName = nameParts[0] || "";
        const lastName = nameParts.slice(1).join(" ") || "";

        // Financial normalization (fallbacks)
        const montant = record.consultationFee ?? 0;
        const sommePaye = record.amountPaid ?? 0;
        const sommeImpaye =
          record.amountDue ??
          Math.max(0, (record.consultationFee ?? 0) - (record.amountPaid ?? 0));

        const motif = record.motif || "";

        const consultation: Consultation = {
          id: record.id,
          patientId: record.patientId || 0,
          fullName,
          firstName,
          lastName,
          phone,
          dateOfBirth: "", // Not provided by MedicalRecordResponseDTO
          dateVisit: record.dateCreation || "",
          motif,
          doctorId: record.doctorId ? String(record.doctorId) : "",
          doctorName,
          montant,
          sommePaye,
          sommeImpaye,
          maladeId: record.patientId || 0,
          patient: undefined,
          doctor: undefined,
          examinationDate: undefined,
        };
        return consultation;
      });

      setConsultations(consultationsWithInfo);
      setTotalPages(totalPagesData);
      setLoading(false);
    } catch (err) {
      setError("Erreur lors du chargement des consultations");
      setLoading(false);
      console.error("Error fetching consultations:", err);
    }
  };

  const handleViewConsultation = async (consultationId: number | undefined) => {
    const id = consultationId || 0;
    try {
      await axiosInstance.get(API_ENDPOINTS.PATIENTS.GET_BY_ID(id));
      navigate(`/admin/dashboard/patient/view/${id}`);
    } catch (err) {
      console.error("Error navigating to consultation view:", err);
      setError(
        "Erreur lors de la navigation vers les détails de la consultation"
      );
    }
  };

  const handleUpdateConsultation = async (
    consultationId: number | undefined
  ) => {
    const id = consultationId || 0;
    try {
      await axiosInstance.get(API_ENDPOINTS.PATIENTS.GET_BY_ID(id));
      navigate(`/admin/dashboard/patient/edit/${id}`);
    } catch (err) {
      console.error("Error navigating to consultation update:", err);
      setError(
        "Erreur lors de la navigation vers la modification de la consultation"
      );
    }
  };

  // Helper function to get doctor name by ID
  const getDoctorName = (doctorId: number): string => {
    const doctor = doctors.find((d) => d.id === doctorId);
    return doctor
      ? `${doctor.firstName} ${doctor.lastName}`
      : `Dr. ${doctorId}`;
  };

  const toYMD = (dateString: string) => {
    if (!dateString) return "";
    const d = new Date(dateString);
    if (isNaN(d.getTime())) return "";
    return d.toISOString().slice(0, 10);
  };

  const isDateMatch = (dateVisit: string) => {
    if (dateFilterMode === "all") return true;
    const ymd = toYMD(dateVisit);
    const target = dateFilterMode === "today" ? todayYMD : selectedDate;
    return ymd === target;
  };

  const formatDate = (dateString: string | number | undefined) =>
    dateString ? new Date(dateString).toLocaleDateString("fr-FR") : "";

  const handleCreateConsultationForPatient = (consultation: Consultation) => {
    navigate("/admin/dashboard/patient/new", {
      state: { selectedPatientId: consultation.maladeId },
    });
  };

  // Client-side filtering for additional filters (date, montant, paye, etc.)
  // Note: Server-side search handles the main search term
  const filteredConsultations = consultations.filter((consultation) => {
    const matchesDate = isDateMatch(consultation.dateVisit);
    const matchesMontant =
      filterMontant === null || consultation.montant >= filterMontant;
    const matchesPaye =
      filterPaye === null
        ? true
        : filterPaye
          ? consultation.sommeImpaye === 0
          : consultation.sommeImpaye > 0;

    const matchesPhone =
      !phoneFilter ||
      (consultation.phone || "")
        .toLowerCase()
        .includes(phoneFilter.toLowerCase());
    const matchesMotif =
      !motifFilter ||
      (consultation.motif || "")
        .toLowerCase()
        .includes(motifFilter.toLowerCase());
    const matchesName =
      !nameFilter ||
      (consultation.fullName || "")
        .toLowerCase()
        .includes(nameFilter.toLowerCase());
    const matchesDoctor =
      !doctorFilter ||
      (consultation.doctorName || "")
        .toLowerCase()
        .includes(doctorFilter.toLowerCase());

    return (
      matchesDate &&
      matchesMontant &&
      matchesPaye &&
      matchesPhone &&
      matchesMotif &&
      matchesName &&
      matchesDoctor
    );
  });

  // Pagination controls - now using server-side pagination
  const paginate = (pageNumber: number) => setCurrentPage(pageNumber);
  const nextPage = () =>
    setCurrentPage((prev) => Math.min(prev + 1, totalPages));
  const prevPage = () => setCurrentPage((prev) => Math.max(prev - 1, 1));
  const firstPage = () => setCurrentPage(1);
  const lastPage = () => setCurrentPage(totalPages);

  if (loading)
    return (
      <LoadingScreen message="Chargement des consultations..." role="admin" />
    );
  if (error) return <ResponseMessage message={error} role="admin" />;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100">
      <Header
        title="Gestion des Consultations"
        description="Visualisez et gérez les informations des consultations."
        action={{
          label: "Nouvelle Consultation",
          to: "/admin/dashboard/patient/new",
          icon: <Plus className="w-4 h-4" />,
        }}
        role="admin"
      />
      <div className="px-6">
        <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
          <div className="flex flex-col gap-4 mb-6">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="relative flex-grow">
                <Search
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                  size={18}
                />
                <input
                  type="text"
                  placeholder="Rechercher globalement (nom, motif, téléphone, docteur)"
                  className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-gray-50 text-gray-800 placeholder-gray-500 transition-colors"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              <div className="flex gap-2">
                <select
                  className="px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-gray-50 text-gray-800 transition-colors"
                  value={dateFilterMode}
                  onChange={(e) => setDateFilterMode(e.target.value as any)}
                >
                  <option value="today">Aujourd'hui</option>
                  <option value="custom">Date précise</option>
                  <option value="all">Toutes</option>
                </select>
                {dateFilterMode === "custom" && (
                  <input
                    type="date"
                    className="px-3 py-2 border border-gray-200 rounded-lg bg-gray-50"
                    value={selectedDate}
                    onChange={(e) => setSelectedDate(e.target.value)}
                  />
                )}
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
              <input
                type="text"
                placeholder="Filtrer par téléphone"
                className="px-3 py-2 border border-gray-200 rounded-lg bg-gray-50"
                value={phoneFilter}
                onChange={(e) => setPhoneFilter(e.target.value)}
              />
              <input
                type="text"
                placeholder="Filtrer par motif"
                className="px-3 py-2 border border-gray-200 rounded-lg bg-gray-50"
                value={motifFilter}
                onChange={(e) => setMotifFilter(e.target.value)}
              />
              <input
                type="text"
                placeholder="Filtrer par nom"
                className="px-3 py-2 border border-gray-200 rounded-lg bg-gray-50"
                value={nameFilter}
                onChange={(e) => setNameFilter(e.target.value)}
              />
              <select
                className="px-3 py-2 border border-gray-200 rounded-lg bg-gray-50"
                value={doctorFilter}
                onChange={(e) => setDoctorFilter(e.target.value)}
              >
                <option value="">Tous les docteurs</option>
                {doctors.map((d) => (
                  <option
                    key={d.id}
                    value={`${d.firstName} ${d.lastName}`.toLowerCase()}
                  >
                    {d.firstName} {d.lastName}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex flex-col md:flex-row gap-2">
              <select
                className="px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-gray-50 text-gray-800 transition-colors"
                value={filterMontant === null ? "" : filterMontant}
                onChange={(e) =>
                  setFilterMontant(
                    e.target.value ? Number(e.target.value) : null
                  )
                }
              >
                <option value="">Tous les montants</option>
                <option value="10000">10,000 MRU+</option>
                <option value="20000">20,000 MRU+</option>
                <option value="30000">30,000 MRU+</option>
              </select>
              <select
                className="px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-gray-50 text-gray-800 transition-colors"
                value={
                  filterPaye === null ? "" : filterPaye ? "paye" : "impaye"
                }
                onChange={(e) =>
                  setFilterPaye(
                    e.target.value === "" ? null : e.target.value === "paye"
                  )
                }
              >
                <option value="">Tous les statuts</option>
                <option value="paye">Payé intégralement</option>
                <option value="impaye">Avec impayés</option>
              </select>
            </div>
          </div>
          <div className="overflow-x-auto -mx-2 sm:-mx-4 px-2 sm:px-4">
            <table className="w-full table-auto divide-y divide-gray-200 min-w-[600px]">
              <thead className="bg-gray-50 sticky top-0 z-10">
                <tr>
                  {[
                    { label: "Nom complet", class: "md:w-1/6" },
                    {
                      label: "Téléphone",
                      class: "hidden lg:table-cell md:w-1/8",
                    },
                    {
                      label: "Date de naissance",
                      class: "hidden lg:table-cell md:w-1/8",
                    },
                    { label: "Date visite", class: "md:w-1/8" },
                    { label: "Motif", class: "md:w-1/6" },
                    { label: "Docteur", class: "md:w-1/6" },
                    { label: "Montant", class: "md:w-1/8" },
                    { label: "Payé", class: "md:w-1/8" },
                    { label: "Impayé", class: "md:w-1/8" },
                    { label: "Actions", class: "md:w-1/8" },
                  ].map((header, index) => (
                    <th
                      key={index}
                      scope="col"
                      className={`px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider ${header.class}`}
                    >
                      {header.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredConsultations.length > 0 ? (
                  filteredConsultations.map((consultation) => (
                    <tr
                      key={consultation.id}
                      className="hover:bg-gray-50 transition-colors"
                    >
                      <td className="px-4 py-3 whitespace-nowrap font-medium text-gray-900 text-sm">
                        {consultation.firstName} {consultation.lastName}
                      </td>

                      <td className="px-4 py-3 whitespace-nowrap text-gray-500 text-sm hidden lg:table-cell">
                        {consultation.phone}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-gray-500 text-sm hidden lg:table-cell">
                        {formatDate(consultation.dateOfBirth)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-gray-500 text-sm">
                        {formatDate(consultation.dateVisit)}
                      </td>
                      <td className="px-4 py-3 text-gray-500 text-sm truncate max-w-[150px]">
                        {consultation.motif}
                      </td>
                      <td className="px-4 py-3 text-gray-500 text-sm truncate max-w-[200px]">
                        {consultation.doctorName ||
                          getDoctorName(Number(consultation.doctorId))}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-gray-500 text-sm">
                        {consultation.montant?.toLocaleString() || "0"} MRU
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-green-600 font-medium text-sm">
                        {consultation.sommePaye?.toLocaleString() || "0"} MRU
                      </td>
                      <td
                        className={`px-4 py-3 whitespace-nowrap font-medium text-sm ${(consultation.sommeImpaye || 0) > 0
                          ? "text-red-600"
                          : "text-gray-500"
                          }`}
                      >
                        {consultation.sommeImpaye?.toLocaleString() || "0"} MRU
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-center">
                        <div className="flex justify-center space-x-2">
                          <button
                            className="p-1.5 rounded-full bg-blue-50 text-blue-600 hover:bg-blue-100 hover:text-blue-800 transition-colors"
                            onClick={() =>
                              handleViewConsultation(consultation.maladeId)
                            }
                            aria-label="Voir la consultation"
                          >
                            <Eye size={16} />
                          </button>
                          <button
                            className="p-1.5 rounded-full bg-yellow-50 text-yellow-600 hover:bg-yellow-100 hover:text-yellow-800 transition-colors"
                            onClick={() =>
                              handleUpdateConsultation(consultation.maladeId)
                            }
                            aria-label="Modifier la consultation"
                          >
                            <Pencil size={16} />
                          </button>
                          <button
                            className="p-1.5 rounded-full bg-green-50 text-green-600 hover:bg-green-100 hover:text-green-800 transition-colors"
                            onClick={() =>
                              handleCreateConsultationForPatient(consultation)
                            }
                            aria-label="Nouvelle consultation pour ce patient"
                          >
                            <Plus size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan={10}
                      className="px-4 py-3 text-center text-gray-500 text-sm"
                    >
                      Aucune consultation trouvée
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <div className="px-4 py-3 bg-gray-50 border-t border-gray-200 flex flex-col md:flex-row justify-between items-center">
            <div className="text-sm text-gray-600 mb-4 md:mb-0">
              Affichage de {filteredConsultations.length} consultations (Page{" "}
              {currentPage} sur {totalPages})
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={firstPage}
                disabled={currentPage === 1}
                className={`p-1.5 rounded-md ${currentPage === 1
                  ? "text-gray-400 cursor-not-allowed"
                  : "text-gray-700 hover:bg-gray-100"
                  }`}
                aria-label="Première page"
              >
                <ChevronsLeft size={16} />
              </button>
              <button
                onClick={prevPage}
                disabled={currentPage === 1}
                className={`p-1.5 rounded-md ${currentPage === 1
                  ? "text-gray-400 cursor-not-allowed"
                  : "text-gray-700 hover:bg-gray-100"
                  }`}
                aria-label="Page précédente"
              >
                <ChevronLeft size={16} />
              </button>
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                const pageNum =
                  totalPages <= 5
                    ? i + 1
                    : currentPage <= 3
                      ? i + 1
                      : currentPage >= totalPages - 2
                        ? totalPages - 4 + i
                        : currentPage - 2 + i;
                return (
                  <button
                    key={pageNum}
                    onClick={() => paginate(pageNum)}
                    className={`w-8 h-8 rounded-md text-sm ${currentPage === pageNum
                      ? "bg-blue-600 text-white"
                      : "text-gray-700 hover:bg-gray-100"
                      }`}
                    aria-label={`Page ${pageNum}`}
                  >
                    {pageNum}
                  </button>
                );
              })}
              <button
                onClick={nextPage}
                disabled={currentPage === totalPages}
                className={`p-1.5 rounded-md ${currentPage === totalPages
                  ? "text-gray-400 cursor-not-allowed"
                  : "text-gray-700 hover:bg-gray-100"
                  }`}
                aria-label="Page suivante"
              >
                <ChevronRight size={16} />
              </button>
              <button
                onClick={lastPage}
                disabled={currentPage === totalPages}
                className={`p-1.5 rounded-md ${currentPage === totalPages
                  ? "text-gray-400 cursor-not-allowed"
                  : "text-gray-700 hover:bg-gray-100"
                  }`}
                aria-label="Dernière page"
              >
                <ChevronsRight size={16} />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PatientForm;
